import React from 'react';
import { useIntl } from 'react-intl';
import { Input, ModalBody, ModalFooter } from 'reactstrap';
import {
  SOUNDCLOUD_DESCRIPTION_MAX_LENGTH,
  SOUNDCLOUD_TITLE_MAX_LENGTH,
  type SongPublishDefaultsResponse,
} from 'fym-shared/src/music24/songPublish';
import { TypedMemo } from '../../../common/types';
import { soundCloudUploadMessages as m } from '../soundCloudMessages';
import { useSongUploadForm } from './useSongUploadForm';
import { SongUploadGoesTo } from './SongUploadGoesTo';
import { SongUploadFacts } from './SongUploadFacts';
import { SongUploadChoice } from './SongUploadChoice';
import { SongUploadWhen } from './SongUploadWhen';
import { songUploadErrorText } from './songUploadErrorText';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for SongUpload; each part uses its own slice of it */
import styles from './SongUpload.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongUploadFormProps {
  accountId: number;
  defaults: SongPublishDefaultsResponse;
  onOpenCatalog: () => void;
  onClose: () => void;
}

/** The upload dialog (design c3 upcoming song, c4 released song). The facts are read only. */
const SongUploadFormComponent: React.FC<SongUploadFormProps> = ({ accountId, defaults, onOpenCatalog, onClose }) => {
  const intl = useIntl();
  const form = useSongUploadForm({ accountId, defaults, onDone: onClose });
  let go = intl.formatMessage(m.goNow);
  if (form.timing.timing === 'releaseDay') {
    go = intl.formatMessage(m.goReleaseDay);
  } else if (form.timing.timing === 'later') {
    go =
      form.publishAt === null
        ? intl.formatMessage(m.goLaterNoTime)
        : intl.formatMessage(m.goLater, {
            time: intl.formatDate(form.publishAt, {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            }),
          });
  }

  return (
    <>
      <ModalBody className={styles.body} data-testid="song-upload-form">
        <SongUploadGoesTo connection={defaults.connection} onOpenCatalog={onOpenCatalog} />
        <div className={styles.twoCols}>
          <label className={styles.box} htmlFor="song-upload-title">
            <span className={styles.label}>{intl.formatMessage(m.fieldTitle)}</span>
            <Input
              id="song-upload-title"
              value={form.title}
              maxLength={SOUNDCLOUD_TITLE_MAX_LENGTH}
              onChange={(event) => form.setTitle(event.target.value)}
            />
          </label>
          <label className={styles.box} htmlFor="song-upload-artist">
            <span className={styles.label}>{intl.formatMessage(m.fieldArtist)}</span>
            <Input id="song-upload-artist" value={defaults.facts.artistName} readOnly disabled />
          </label>
        </div>
        <SongUploadFacts facts={defaults.facts} />
        <fieldset className={styles.box}>
          <legend className={styles.label}>{intl.formatMessage(m.whoCanListen)}</legend>
          <div className={styles.twoCols}>
            <SongUploadChoice
              name="song-upload-sharing"
              value="public"
              checked={form.sharing === 'public'}
              title={intl.formatMessage(m.public)}
              hint={intl.formatMessage(m.publicHint)}
              onSelect={() => form.setSharing('public')}
            />
            <SongUploadChoice
              name="song-upload-sharing"
              value="private"
              checked={form.sharing === 'private'}
              title={intl.formatMessage(m.private)}
              hint={intl.formatMessage(m.privateHint)}
              onSelect={() => form.setSharing('private')}
            />
          </div>
        </fieldset>
        <SongUploadWhen
          timings={form.timings}
          value={form.timing}
          sharing={form.sharing}
          releaseDate={defaults.facts.releaseDate}
          onChange={form.changeTiming}
        />
        <label className={styles.box} htmlFor="song-upload-description">
          <span className={styles.label}>{intl.formatMessage(m.description)}</span>
          <Input
            id="song-upload-description"
            type="textarea"
            rows={3}
            value={form.description}
            maxLength={SOUNDCLOUD_DESCRIPTION_MAX_LENGTH}
            onChange={(event) => form.setDescription(event.target.value)}
          />
          <span className={styles.hint}>{intl.formatMessage(m.descriptionHint)}</span>
        </label>
      </ModalBody>
      <ModalFooter className={styles.foot}>
        <span className={styles.footNote}>{withBrandWord(intl.formatMessage(m.footNote))}</span>
        <button type="button" className="btn btn-link" onClick={onClose}>
          {intl.formatMessage(m.notNow)}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={form.isSubmitting}
          data-testid="song-upload-submit"
          onClick={() => form.submit().catch(console.error)}
        >
          {go}
        </button>
        {form.error !== null && (
          <span className={styles.error} role="alert">
            {withBrandWord(songUploadErrorText(intl, form.error))}
          </span>
        )}
      </ModalFooter>
    </>
  );
};

export const SongUploadForm = TypedMemo(SongUploadFormComponent);
